/* eslint-disable @typescript-eslint/no-deprecated -- DEPRECATION: [APPDEV-17834] BREAKING-CHANGE: migrate to ChartTooltip before next major. */
import { useIntl } from 'react-intl';

import { wrapper } from './HistogramTooltip.sty.js';
import { HistogramTooltipWrapper } from './HistogramTooltipWrapper.js';
import { Divider } from '../../../../../layouts/divider/Divider.js';
import { ChartActionsMenu } from '../../../../core/components/series-action/ChartActionsMenu.js';
import { useChartActionsMenu } from '../../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { getDefaultAction } from '../../../../core/components/series-action/hooks/useDefaultAction.js';
import { InteractiveChartTooltip } from '../../../../core/components/tooltip/InteractiveChartTooltip/components/InteractiveChartTooltip.js';
import { SeriesBasedContent } from '../../../../core/components/tooltip/InteractiveChartTooltip/contents/SeriesBasedContent.js';
import { CategoricalTooltipHeader } from '../../../../core/components/tooltip/InteractiveChartTooltip/headers/CategoricalTooltipHeader.js';
import { useTooltipSeriesActions } from '../../../../core/components/tooltip/shared/hooks/useTooltipSeriesActions.js';
import { Formatter } from '../../../../core/types/formatter.js';
import { useHistogramFilteredData } from '../../../hooks/useHistogramFilteredData.js';
import { useHistogramTooltip } from '../../../hooks/useHistogramTooltip.js';
import { useHistogramTruncationMode } from '../../../hooks/useHistogramTruncationMode.js';
import { useHistogramXFormatter } from '../../../hooks/useHistogramXFormatter.js';
import { useHistogramYFormatter } from '../../../hooks/useHistogramYFormatter.js';
import { useSeriesActionClickHandler } from '../../../hooks/useSeriesActionClickHandler.js';
import { useXAxisConfig } from '../../../hooks/useXAxisConfig.js';
import { safeGet } from '../../../providers/HistogramDataProvider.js';
import {
  HistogramBin,
  HistogramSeries,
  WeakMapBins,
} from '../../../types/histogram.js';
import { PREPOSITION } from '../constants.js';
import {
  type HistogramTooltipDataPoint,
  toTooltipItems,
} from '../utils/bins-to-tooltip.js';
import { getHeaderLabel } from '../utils/get-header-label-tooltip.js';

interface HistogramTooltipProps {
  points: HistogramTooltipDataPoint[];
  closestPoint?: HistogramTooltipDataPoint;
}

function getHeaderResolution(
  binsMap: WeakMapBins,
  bin: HistogramBin,
  formatter: Formatter,
) {
  const { from, to } = safeGet(binsMap, bin);
  return formatter(to - from);
}

/**
 * Tooltip specific to the Histogram.
 */
export const HistogramTooltip = ({
  points,
  closestPoint,
}: HistogramTooltipProps) => {
  const { binsMap, series, seriesMap } = useHistogramFilteredData();

  const { unit: xUnit } = useXAxisConfig();
  const { variant } = useHistogramTooltip();
  const truncationMode = useHistogramTruncationMode();
  const bottomFormatter = useHistogramXFormatter();
  const {
    onLeave: handleMenuLeave,
    onScroll: handleMenuScroll,
    selectedItem,
    updateSelectedItem,
    actionsMenuRef,
    isScrolling,
  } = useChartActionsMenu();
  const formatters = useHistogramYFormatter();
  const { units } = useHistogramFilteredData();

  const isSingle = variant === 'single';

  const { tooltipItems, closestTooltipItem } = toTooltipItems(
    binsMap,
    formatters,
    isSingle,
    units,
    points,
    closestPoint,
  );

  const actionClickHandler = useSeriesActionClickHandler(
    tooltipItems.length > 1,
  );

  const seriesActions = useTooltipSeriesActions<HistogramSeries>(
    series,
    (series) => safeGet(seriesMap, series).id,
    tooltipItems.find((t) => t.id === selectedItem)?.bin,
  );

  const intl = useIntl();

  const showHeaderTooltipItem = !isSingle && tooltipItems.length > 1;

  const showHeaderDivider = tooltipItems.length > 1 && variant === 'shared';

  const labelFormatter = (value: number) => bottomFormatter(value, xUnit);

  const headerLabel = closestPoint
    ? getHeaderLabel(
        binsMap,
        closestPoint.bin,
        labelFormatter,
        intl.formatMessage(PREPOSITION.to),
      )
    : '';

  return (
    <HistogramTooltipWrapper
      tooltipItems={tooltipItems}
      onMouseLeave={handleMenuLeave}
      onScrollContent={handleMenuScroll}
    >
      <InteractiveChartTooltip.Header>
        {closestPoint && (
          <>
            <CategoricalTooltipHeader
              //TODO Add the X axis unit
              resolution={getHeaderResolution(
                binsMap,
                closestPoint.bin,
                labelFormatter,
              )}
              name={headerLabel}
              item={
                showHeaderTooltipItem
                  ? {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      ...closestTooltipItem!,
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      values: closestTooltipItem!.asyncValues(),
                    }
                  : undefined
              }
            />
            {showHeaderDivider && (
              <div
                className={wrapper}
                key={`divider-${closestTooltipItem?.id}`}
              >
                <Divider />
              </div>
            )}
          </>
        )}
      </InteractiveChartTooltip.Header>

      {tooltipItems && (
        <InteractiveChartTooltip.Body>
          {tooltipItems.map((item) => () => {
            const isOpen = selectedItem === item.id;
            return (
              <ChartActionsMenu
                key={`${item.id}`}
                isOpen={isOpen}
                actions={isOpen ? seriesActions(item) : undefined}
                defaultActions={getDefaultAction(item)}
                ref={actionsMenuRef}
                onActionClick={actionClickHandler}
              >
                <SeriesBasedContent
                  item={{ ...item, values: item.asyncValues() }}
                  onMouseHover={() => updateSelectedItem(item.id)}
                  truncationMode={truncationMode}
                  isScrolling={isScrolling}
                />
              </ChartActionsMenu>
            );
          })}
        </InteractiveChartTooltip.Body>
      )}
    </HistogramTooltipWrapper>
  );
};
